import { IAttendeeDTO, IAttendeeOrganizationDTO } from './attendees.dao';
import { IOperatorsDTO, IUserOperatorDTO } from './operators.dao';
import { IOperatorDetailsDTO, IRoomDTO } from './rooms.dao';
import { Access, MeetingStatus } from './meetings.dao';
import { IEventRequestParams, IImageWithObjectKey, IImageWithUrl } from './shared.dto';
import { IConnectionExportTemplate } from './users.dao';
import { JobDtoType } from '../domain/jobs';
import { OrganizationMeetingConnection } from '../modules/connections/meeting/organization-meeting-connection.aggregate';
import { ConnectionEntity } from '../modules/connections/abstract-connection.entity';
import { OrganizationRole } from '../modules/connections/types';
import { ApprovalType } from '../components/_events/_manage-event/shared/connections-list/_create-zoom-webinar-modal/create-zoom-webinar-form-data.viewmodel';
import { MeetingApiParams } from '../components/_events/_manage-event/shared/connections-list/_connection-form/connection-form.viewmodel';
import { ConnectionVersion } from '../constants/common.constants';

abstract class IConnectionsDAO {
    public abstract getMeetingConnections(params: IMeetingConnectionRequestParams): Promise<IMeetingConnectionsDTO>;
    public abstract getRoomsConnectionsList(params: IRoomConnectionsRequestParams): Promise<IRoomConnectionsDTO[]>;
    public abstract getMeetingConnectionsByRoom(params: IMeetingConnectionsRequestParams): Promise<IRoomMeetingConnectionsDTO[]>;
    public abstract getConnectionDatesList(params: IEventRequestParams): Promise<{ dates: Date[] }>;
    public abstract getConnectionTypes(params: IEventRequestParams): Promise<IConnectionTypesDTO>;
    public abstract updateMeetingConnections(params: IEventRequestParams & IMeetingConnectionsDTO): Promise<IMeetingConnectionsDTO>;
    public abstract createMeetingConnection(params: IMeetingConnectionRequestParams & IConnectionDetailsDTO): Promise<IConnectionDetailsDTO>;
    public abstract editMeetingConnection(params: IMeetingConnectionRequestParams & IConnectionDetailsDTO): Promise<IConnectionDetailsDTO>;
    public abstract createRoomConnection(params: IRoomConnectionRequestParams & IConnectionDetailsDTO): Promise<IConnectionDetailsDTO>;
    public abstract editRoomConnection(params: IRoomConnectionRequestParams & IConnectionDetailsDTO): Promise<IConnectionDetailsDTO>;
    public abstract deleteRoomConnection(params: IRoomConnectionRequestParams & IDeleteConnectionRequestParams): Promise<void>;
    public abstract deleteMeetingConnection(params: IDeleteConnectionRequestParams & IMeetingConnectionRequestParams): Promise<IMeetingConnectionsDTO>;
    public abstract getConnectionsCSV(params: IEventRequestParams): Promise<void>;
    public abstract getAllEventConnectionsCSV(params: IEventRequestParams & IConnectionExportTemplate): Promise<void>;
    public abstract getWebinarParticipantConnections(params: IMeetingConnectionRequestParams & IEventRequestParams): Promise<IWebinarParticipantConnections>;
    public abstract getKnovioConnectionDetails(params: IMeetingConnectionRequestParams & IMeetingConnectionIdParam): Promise<IConnectionDetailsDTO>;
    public abstract getKnovioRoomConnectionDetails(params: IRoomConnectionRequestParams & IMeetingConnectionIdParam): Promise<IConnectionDetailsDTO>;
    public abstract operatorsBulkUpdate(params: IEventRequestParams & IOperatorsBulkUpdateDTO): Promise<JobDtoType>;
    public abstract updateMeetingConnectionsWithSingleEntity(
        params: IEventRequestParams & IMeetingConnectionsSingleEntityDTO
    ): Promise<IMeetingConnectionsSingleResponceDTO>;
    public abstract getConnectionsOperatorsByMeetingIds(params: IEventRequestParams & { meetingIds: string }): Promise<IMeetingsOperatorsDTO>;
    public abstract createKnovioLiveStream(
        params: MeetingApiParams,
        payload: IKnovioLiveStreamDTO
    ): Promise<IConnectionDetailsDTO>;
    public abstract updateKnovioLiveStream(
        params: MeetingApiParams,
        payload: IKnovioLiveStreamDTO
    ): Promise<{ message: string }>;
    public abstract generateConnections(params: TGenerateConnectionsRequest): Promise<JobDtoType>;
    public abstract syncSpeakersToKnovio(params: MeetingApiParams & ISyncSpeaekrsToKnovioDTO): Promise<ISyncSpeaekrsToKnovioDTO>;
    public abstract deleteUniqueJoiningLinksConnections(params: IEventRequestParams & { meetingId: number }): Promise<JobDtoType>;
    public abstract scheduleNewAudioBridge(params: IEventRequestParams & IAudioBridgeDTO): Promise<IAudioBridge>;
    public abstract createMeetingConnectionGroup(params: IMeetingConnectionRequestParams & IConnectionGroupDTO): Promise<IConnectionGroupDTO>;
    public abstract editMeetingConnectionGroup(params: IUpdateMeetingConnectionGroup & IConnectionGroupDTO): Promise<IConnectionGroupDTO>;
    public abstract deleteMeetingConnectionGroup(params: IUpdateMeetingConnectionGroup): Promise<void>;
    public abstract createRoomConnectionGroup(params: IRoomConnectionRequestParams & IConnectionGroupDTO): Promise<IConnectionGroupDTO>;
    public abstract editRoomConnectionGroup(params: IUpdateRoomConnectionGroup & IConnectionGroupDTO): Promise<IConnectionGroupDTO>;
    public abstract deleteRoomConnectionGroup(params: IUpdateRoomConnectionGroup): Promise<void>;
    public abstract getConnectionPassportConfigureDisplay(params: IEventRequestParams & IConfigurePassportDisplayParams): Promise<IConfigurePassportDisplayDTO>;
    public abstract updateConnectionPassportConfigureDisplay(
        params: IEventRequestParams & IConfigurePassportDisplayParams & IConfigurePassportDisplayDTO): Promise<IConfigurePassportDisplayDTO>;
    public abstract getRoomConnectionPassportConfigureDisplay(
        params: IEventRequestParams & IConfigurePassportDisplayParams): Promise<IConfigurePassportDisplayDTO>;
    public abstract updateRoomConnectionPassportConfigureDisplay(
        params: IEventRequestParams & IConfigurePassportDisplayParams & IConfigurePassportDisplayDTO): Promise<IConfigurePassportDisplayDTO>;
    public abstract scheduleNewPanelistLink(params: IPanelistLinkConnectionInfo): Promise<IPanelistLinkResponse>;
    public abstract updateChildConnection(params: IEventRequestParams & Partial<IChildConnectionDTO>): Promise<IChildConnectionDTO>;
    public abstract deleteChildConnection(params: IEventRequestParams & { joinMethodId: number }): Promise<void>;
}

enum ConnectionType {
    Zoom = 'Zoom',
    Webex = 'Webex',
    Teams = 'Teams',
    Bluejeans = 'Bluejeans',
    Knovio = 'Knovio',
    Other = 'Other',
    InPerson = 'In-Person',
    Showcase = 'Showcase',
    Webinar = 'Webinar',
    UniqueJoiningLink = 'Unique Joining Link',
    UniqueDialIn = 'Unique Dial In',
    Veracast = 'Veracast',
    AudioBridge = 'Audio Bridge',
    PanelistLink = 'Panelist Link',
}

export enum ConnectionStatus {
    STARTED = 'STARTED',
    NOT_STARTED = 'NOT_STARTED',
}

export enum KnovioLabel {
    OEStream = 'OE Stream',
    Project = 'Project',
}

interface IConnectionOption {
    id: number | null;
    value: string;
    audioBridgeID?: string;
}

export enum AudioBridgeProvider {
    ZOOM = 'ZOOM',
    TURBOBRIDGE = 'TURBOBRIDGE',
    OTHER = 'OTHER',
}

export enum ConferenceModes {
    Presentation = 'Presentation',
    QA = 'Q&A',
    Conversation = 'Conversation',
    Hosts = 'Hosts Only',
    'Q&A' = 'Q_AND_A',
    'Hosts Only' = 'HOSTS_ONLY',
}

export enum WebinarAutoRecording {
    Cloud = 'cloud',
    None = 'none'
}

interface IConnectionDetailsBase {
    meetingConnectionId: number;
    type: ConnectionType;
    joinMethodId: number;
    connectionNotes: string;
    connectionStatus: ConnectionStatus;
    cancelDateTime: string;
    blueprintId: string;
    connectionVersion: ConnectionVersion;
    errorMessage: string;
    cancelled: boolean;
    draft: boolean;
}

interface IZommRelatedConnectionDetails extends IConnectionDetailsBase {
    connectionLink: string;
    alternativeLink: string;
    connectionId: string;
    connectionPassword: string;
    connectionAccount: string;
    sip: string;
    connectionHostKey: string;
    dialForVs: 'In' | 'Out' | null;
    meetingInvitation: string;
    connectionTopic: string;
    hostLink: string;
    label: string;
    registrationEnabled: boolean;
    meetingUniqueReference: number | string;
}

interface IAudioBridgeConnectionDetails extends IZommRelatedConnectionDetails {
    provider: AudioBridgeProvider;
    participantTelephoneNumbers: string;
    participantOneTapDial: string;
    conferenceId: string;
    participantId: string;
    operatorTelephoneNumber: string;
    operatorPin: string;
    audioBridgeID: string;
    audioProviderId: string;
    audioBridgeName: string;
    autoSyncEnabled: boolean;
    automaticallyRecordEnabled: boolean;
}

interface IPanelistLinkConnectionDetails extends IZommRelatedConnectionDetails {
    webinarId: string;
    attendeeId: number;
    attendeeName: string;
    participantId: string;
    email: string;
    parentJoinMethodId?: number;
    parentMeetingConnectionId?: number;
}

interface IBluejeansConnectionDetails extends IZommRelatedConnectionDetails { }
interface ITeamsConnectionDetails extends IZommRelatedConnectionDetails { }
interface IWebexConnectionDetails extends IZommRelatedConnectionDetails { }

interface IInPersonConnectionDetails extends IConnectionDetailsBase {
    name: string;
    meetingLocation: string;
}

interface IOtherConnectionDetails extends IZommRelatedConnectionDetails {
    name: string;
    passportReplayEnabled: boolean;
}

interface IKnovioConnectionDetails extends IConnectionDetailsBase {
    accountName: string;
    knovioName: string;
    embedCodeId: string;
    primaryLiveStreamUrl: string;
    primaryLiveStreamName: string;
    backupLiveStreamUrl: string;
    backupLiveStreamName: string;
    detailsPlaceholderText: string;
    knovioAccountId: number;
    knovioPresentationId: number;
    podiumLink: string;
    podiumAccessCode: string;
    podiumEmbedCode: string;
    knovioEditMetadata: IKnovioEditMetadata;
    passportReplayEnabled: boolean;
    publishEnabled: boolean;
    liveStreamEnabled: boolean;
}
interface IShowcaseConnectionDetails extends IConnectionDetailsBase {
    showcasePagePath: string;
    showcaseName: string;
    accountName: string;
}

interface IUniqueJoiningLinkConnectionDetails extends IZommRelatedConnectionDetails {
    attendeeEmail: string;
    zoomDescription: string;
}

interface IVeracastConnectionDetails extends IConnectionDetailsBase {
    veracastEventID: string;
    veracastConfID: string;
    connectionLink: string;
    connectionPassword: string;
    clientAppLink: null;
}
interface IZoomConnectionDetails extends IZommRelatedConnectionDetails {
    zoomDescription: string;
    approvalType: boolean | ApprovalType;
    registrantsEmailNotification: boolean;
    registrantsConfirmationEmail: boolean;
    autoRegistrationEnabled: boolean;
    autoSyncEnabled: boolean;
}
interface IWebinarConnectionDetails extends IZoomConnectionDetails {
    registrationUrl: string;
    autoRecording: string;
    childConnections: IChildConnectionDTO[];
}

type IConnectionDetailsDTO =
    & Partial<IAudioBridgeConnectionDetails>
    & Partial<IBluejeansConnectionDetails>
    & Partial<ITeamsConnectionDetails>
    & Partial<IWebexConnectionDetails>
    & Partial<IInPersonConnectionDetails>
    & Partial<IOtherConnectionDetails>
    & Partial<IKnovioConnectionDetails>
    & Partial<IShowcaseConnectionDetails>
    & Partial<IUniqueJoiningLinkConnectionDetails>
    & Partial<IVeracastConnectionDetails>
    & Partial<IZoomConnectionDetails>
    & Partial<IWebinarConnectionDetails>
    & Partial<IPanelistLinkConnectionDetails>;

interface IKnovioEditMetadata {
    metadata: IKnovioMetadata;
    template: IKnovioTemplateOptions | null;
    layout: LayoutOption | null;
    playbackSettings: IKnovioPlaybackSettings;
    embedCodeId: string;
    accountName: string;
    broadcastDate: Date;
    broadcastTimeZone: string;
    podiumLink: string;
    podiumAccessCode: string;
    qaNotificationEmailRecipients: string;
}

interface IKnovioTemplateOptions {
    enableOverview: boolean;
    enableQAndA: boolean;
    enableSpeakers: boolean;
}

interface IKnovioPlaybackSettings {
    enableDvrNavigation: boolean;
}

interface IKnovioMetadata {
    title: string;
    description: string;
    standByMessage: string;
    expirationDate: Date | null;
}

interface IConnectionAttendeeDTO extends IAttendeeDTO {
    meetingConnectionId?: number;
    meetingConnectionType?: string;
    attendeOrganizationRole?: OrganizationRole;
}

interface IConnectionOrganizationDTO extends IAttendeeOrganizationDTO {
    attendees: IConnectionAttendeeDTO[];
}

interface IParentConnectionDTO {
    meetingConnectionId: number;
    children: IChildConnectionDTO[];
}

interface IChildConnectionDTO
    extends Partial<IUniqueJoiningLinkConnectionDetails>,
        Partial<IAudioBridgeConnectionDetails>,
        Partial<IPanelistLinkConnectionDetails> {
    attendeeId: number;
    parentConnectionId?: number;
    parentJoinMethodId?: number;
    uniqueDialInCode?: string;
    userId?: string;
}

interface IMeetingConnectionsDTO {
    meetingId: number;
    startDateTime: string;
    endDateTime: string;
    status: MeetingStatus;
    meetingAccess: Access;
    defaultConnectionId?: number | null;
    presenterRoleConnectionId?: number | null;
    hostRoleConnectionId?: number | null;
    supportRoleConnectionId?: number | null;
    connections: IConnectionDetailsDTO[];
    presenters: IConnectionOrganizationDTO[];
    hosts: IConnectionOrganizationDTO[];
    participants: IConnectionOrganizationDTO[];
    registrants: IConnectionOrganizationDTO[];
    supports: IConnectionOrganizationDTO[];
    operators: IOperatorsDTO[];
    addedOperators: IOperatorsDTO[];
    removedOperators: IOperatorsDTO[];
    updatedOperators: IOperatorsDTO[];
    room: IRoomDTO;
    meetingRecordEnabled: boolean;
    meetingConnectionNotes: string | null;
    autoRegistrationEnabled: boolean;
    autoSubscriptionEnabled: boolean;
    heroImage?: IImageWithObjectKey;
    thumbnailImage?: IImageWithObjectKey;
    heroImageWithUrl?: IImageWithUrl;
    thumbnailImageWithUrl?: IImageWithUrl;
    biography?: string;
    topic: string;
    blueprintId: string | null;
    connectionGroups?: IConnectionGroupDTO[];
    connectionVersion?: ConnectionVersion;
    parentConnections: IParentConnectionDTO[];
}

interface IConfigurePassportDisplayParams {
    roomId?: number;
    meetingId?: number;
    meetingConnectionId?: number;
    joinMethodId?: number;
}

interface IConfigurePassportDisplayDTO {
    passportLabel: string;
    passportDescription: string;
    connectionDetailsEnabled?: boolean;
    connectButtonLabel?: string;
    showHideButtonEnabled?: boolean;
    showDialInInfo?: string;
    hideDialInInfo?: string;
}

interface IConnectionTypesDTO {
    types: ConnectionType[];
}

interface IMeetingConnectionRequestParams extends IEventRequestParams {
    meetingId: number;
}

interface IRoomConnectionRequestParams extends IEventRequestParams {
    roomId: number;
}

interface IMeetingConnectionIdParam {
    meetingConnectionId: number;
}

interface IDeleteConnectionRequestParams {
    meetingConnectionId: number;
    zoomMeetingDelete?: boolean;
    webinarMeetingDelete?: boolean;
    audioBridgeMeetingDelete?: boolean;
    panelistMeetingDelete?: boolean;
    isPanelistLink?: boolean;
    joinMethodId: number;
    parentMeetingConnectionId?: number;
    connectionVersion?: ConnectionVersion;
}

interface IRoomConnectionsRequestParams extends IEventRequestParams {
    startDateTime: Date;
}
interface IRoomConnectionsDTO {
    roomId: number;
    name: string;
    label: string;
    startDateTime: string;
    endDateTime: string;
    presenters: IAttendeeOrganizationDTO[];
    operators: IOperatorDetailsDTO[];
    connections: IConnectionDetailsDTO[];
    connectionGroups: IConnectionGroupDTO[];
    presenterRoleConnectionId: number | null;
    defaultConnectionId: number | null;
    hostRoleConnectionId: number | null;
    meetingIds: number[] | null;
}
interface IRoomConnections extends IRoomConnectionsDTO {
    meetings?: IRoomMeetingConnectionsDTO[];
    isExpanded?: boolean;
}
interface IMeetingConnectionsRequestParams extends IEventRequestParams {
    roomId?: number;
    startDateTime?: Date;
}
interface IRoomMeetingConnectionsDTO {
    meetingId: number;
    status: MeetingStatus;
    startDateTime: string;
    endDateTime: string;
    defaultConnection?: IConnectionDetailsDTO;
    presenterRoleConnectionId?: number | null;
    hostRoleConnectionId?: number | null;
    supportRoleConnectionId?: number | null;
    connections?: IConnectionDetailsDTO[];
    connectionGroups?: IConnectionGroupDTO[];
    presenters: IConnectionOrganizationDTO[];
    hosts: IConnectionOrganizationDTO[];
    participants: IConnectionOrganizationDTO[];
    registrants: IConnectionOrganizationDTO[];
    supports: IConnectionOrganizationDTO[];
    operators?: IOperatorDetailsDTO[];
    roomLevelOperators?: IOperatorDetailsDTO[];
    meetingRecordEnabled?: boolean;
    replayConnection?: IConnectionDetailsDTO;
}

interface IAllEventConnectionsCSVParams {
    columns: ICsvColumn[];
}

interface ICsvColumn {
    position: number;
    included: boolean;
    header: DefaultColumnValues;
}

enum DefaultColumnValues {
    CONNECTION_LEVEL = 'CONNECTION_LEVEL',
    DATE = 'DATE',
    START_TIME = 'START_TIME',
    END_TIME = 'END_TIME',
    EXTERNAL_ORG_IDS = 'EXTERNAL_ORG_IDS',
    ROOM_PRESENTERS = 'ROOM_PRESENTERS',
    CONNECTION_NOTES = 'CONNECTION_NOTES',
    CONNECTION_TYPE = 'CONNECTION_TYPE',
    CONNECTION_NAME = 'CONNECTION_NAME',
    CONNECTION_LINK = 'CONNECTION_LINK',
    ALTERNATIVE_LINK = 'ALTERNATIVE_LINK',
    HOST_LINK = 'HOST_LINK',
    CONNECTION_ID = 'CONNECTION_ID',
    CONNECTION_PASSCODE = 'CONNECTION_PASSCODE',
    CONNECTION_ACCOUNT = 'CONNECTION_ACCOUNT',
    SIP = 'SIP',
    CONNECTION_HOST_KEY = 'CONNECTION_HOST_KEY',
    DIAL_FOR_VS = 'DIAL_FOR_VS',
    CONNECTION_TOPIC = 'CONNECTION_TOPIC',
    MEETING_INVITATION = 'MEETING_INVITATION',
    SHOWCASE_PAGE_PATH = 'SHOWCASE_PAGE_PATH',
    KNOVIO_ACCOUNT = 'KNOVIO_ACCOUNT',
    KNOVIO_EMBED_CODE_ID = 'KNOVIO_EMBED_CODE_ID',
    KNOVIO_NAME = 'KNOVIO_NAME',
    IS_LIVESTREAM = 'IS_LIVESTREAM',
    PRIMARY_RTMP = 'PRIMARY_RTMP',
    PRIMARY_STREAM_NAME = 'PRIMARY_STREAM_NAME',
    BACKUP_RTMP = 'BACKUP_RTMP',
    BACKUP_STREAM_NAME = 'BACKUP_STREAM_NAME',
    IS_DEFAULT = 'IS_DEFAULT',
    ROOM_NAME = 'ROOM_NAME',
    ROOM_LABEL = 'ROOM_LABEL',
    ROOM_ID = 'ROOM_ID',
    MEETING_ID = 'MEETING_ID',
    ATTENDEE_EMAIL_ADDRESS = 'ATTENDEE_EMAIL_ADDRESS'
}

interface IWebinarParticipantConnections {
    connections: IConnectionDetailsDTO[];
}

interface IOperatorsBulkUpdateDTO {
    operatorsListToAdd: IUserOperatorDTO[];
    meetingIds: number[];
}

interface IMeetingConnectionsSingleResponceDTO {
    operators: IOperatorDetailsDTO[];
}

interface IMeetingConnectionsSingleEntityDTO {
    meetingId: number;
    addedOperators?: IOperatorsDTO[];
    removedOperators?: IOperatorsDTO[];
    updatedOperators?: IOperatorsDTO[];
}

interface IUpdateSingleMeetingPayload {
    roomId: number;
    meetingId: number;
    operators?: IOperatorDetailsDTO[];
    operator?: IOperatorDetailsDTO;
}

type onChangeConnectionEntity = OrganizationMeetingConnection | ConnectionEntity<IConnectionAttendeeDTO>;

interface IMeetingsOperatorsDTO {
    meetingIds: number[];
    meetingOperators: IOperatorsDTO[];
}

export enum FlyoutTabOption {
    Overview = 'overview',
    QuestionsAndAnswers = 'qa',
    Speakers = 'speakers'
}

export enum LayoutOption {
    VideoOnly = 'VIDEO_ONLY',
    VideoLeft = 'VIDEO_LEFT',
    VideoRight = 'VIDEO_RIGHT',
    AudioAndSlides = 'AUDIO_AND_SLIDES'
}

interface INotificationEmail {
    email: string;
}

interface IKnovioLiveStreamDTO {
    title: string;
    description?: string;
    enableNeverlate: boolean;
    enableOverview: boolean;
    enableQAndA: boolean;
    enableSpeakers: boolean;
    layout: LayoutOption;
    qaNotificationEmailRecipients?: INotificationEmail[];
    broadcastDate: string;
    broadcastTimeZone: string;
    standByMessage: string;
    expirationDate: string;
}

interface ICreateKnovioSubscriptionPayload {
    value: {
        data: {
            onUpdateMeetingConnection: IConnectionDetailsDTO;
        };
    };
}

type TGenerateConnectionsRequest = IEventRequestParams & {
    meetingId: number;
    blueprintId: string;
};
interface ISpeakerInfoDTO {
    order: number;
    name: string;
    details: ISpeakerInfoDetails;
    metadata_id?: number;
    modified_at?: number;
}

interface ISpeakerInfoDetails {
    title: string;
    organization: string;
    description: string;
    photo_url?: string;
}

interface ISyncSpeaekrsToKnovioDTO {
    embed_id: string;
    speakers_info: ISpeakerInfoDTO[];
}

interface IAudioBridgeDTO {
    startTime: Date;
    endTime: Date;
    timeZone: string;
    name: string;
    provider?: string;
    conferenceMode: string;
    audioProviderId: string;
    recordCalls?: boolean;
}
interface IAudioBridge {
    audioProviderId: string;
    name: string;
    audioBridgeID: string;
    conferenceID: string;
    operatorPin: string;
}

interface IConnectionJoinMethodDTO {
    joinMethodId: number;
    order: number;
}

interface IConnectionGroupDTO {
    meetingConnectionId: number;
    label: string;
    joinMethods: IConnectionJoinMethodDTO[];
    connectionVersion?: ConnectionVersion.V2_MULTIPLE;
}

interface IUpdateMeetingConnectionGroup extends IMeetingConnectionRequestParams {
    groupId: number;
}

interface IUpdateRoomConnectionGroup extends IRoomConnectionRequestParams {
    groupId: number;
}

interface IAudioBridgeResponse extends IAudioBridge {
    provider: string;
}

interface IPanelistLinkConnectionInfo {
    webinarID: string;
    attendeeID: number;
    attendeeName: string;
    email: string;
    parentJoinMethodId?: number;
    parentMeetingConnectionId?: number;
}

interface IPanelistLinkResponse {
    name: string;
    email: string;
    joinUrl?: string;
    connectionAccount?: string;
}

export {
    IAllEventConnectionsCSVParams,
    IConnectionsDAO,
    IConnectionDetailsDTO,
    IMeetingConnectionsDTO,
    IConnectionTypesDTO,
    IConnectionAttendeeDTO,
    IConnectionOrganizationDTO,
    IMeetingConnectionRequestParams,
    IRoomConnectionRequestParams,
    IDeleteConnectionRequestParams,
    ConnectionType,
    IConnectionOption,
    IRoomConnectionsRequestParams,
    IRoomConnectionsDTO,
    IRoomConnections,
    IMeetingConnectionsRequestParams,
    IRoomMeetingConnectionsDTO,
    DefaultColumnValues,
    ICsvColumn,
    IWebinarParticipantConnections,
    IMeetingConnectionIdParam,
    IOperatorsBulkUpdateDTO,
    IMeetingConnectionsSingleEntityDTO,
    IUpdateSingleMeetingPayload,
    IMeetingConnectionsSingleResponceDTO,
    onChangeConnectionEntity,
    IMeetingsOperatorsDTO,
    IKnovioLiveStreamDTO,
    ICreateKnovioSubscriptionPayload,
    INotificationEmail,
    TGenerateConnectionsRequest,
    ISyncSpeaekrsToKnovioDTO,
    IKnovioEditMetadata,
    IKnovioTemplateOptions,
    IAudioBridge,
    IAudioBridgeDTO,
    IConnectionJoinMethodDTO,
    IConnectionGroupDTO,
    IUpdateMeetingConnectionGroup,
    IUpdateRoomConnectionGroup,
    IConfigurePassportDisplayParams,
    IConfigurePassportDisplayDTO,
    IAudioBridgeResponse,
    IPanelistLinkConnectionInfo,
    IParentConnectionDTO,
    IChildConnectionDTO,
    IPanelistLinkResponse
};
